// ABOUTME: Draws the focused weather's looping vignette scene and title over the stack's head cloud, from the ported v28 vignette data.
// ABOUTME: Under reduced motion every element is drawn in its still pose with no animation, because the stage's reduced-motion CSS overrides animation timing.

import { Box, type SxProps, type Theme } from '@mui/material';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import { CROSSING_HEAD_TITLE_INK, CROSSING_WEATHER_SCENE_HALO } from '../../../../theme/crossingTokens';
import { vignetteFor } from './weatherVignettes';
import { SCENE_IN, WEATHER_KEYFRAMES } from './weatherVignetteKeyframes';
import type { VignetteDelay, VignetteElement } from './weatherVignetteTypes';

// v28's wrDa / wrDb / wrDc in focus.
const DELAY_SECONDS: Record<VignetteDelay, number> = { a: 0, b: 0.35, c: 0.7 };

// v28's scene box: the vignette is authored at this size, then scaled down and centred on the head cloud.
// The offset and scale are v28's, measured against its weather cloud (docs/plans/crossing/evidence/weather-response-reference/excerpts.md).
// They are in the head cloud's own px, so the scene keeps its proportion to the crown at every stack width.
const SCENE_WIDTH = 850;
const SCENE_HEIGHT = 370;
const SCENE_OFFSET_X = -4;
const SCENE_OFFSET_Y = 6.74;
const SCENE_SCALE = 0.6181;

const SERIF = 'Newsreader, serif';

function drawElement(el: VignetteElement, reduced: boolean, key: number) {
  const { anim } = el;
  const motion = !reduced && anim
    // The keyframes object is interpolated, not .name, so emotion injects its CSS when the class is built.
    ? { animation: `${WEATHER_KEYFRAMES[anim.name]} ${anim.seconds}s ${anim.timing} infinite ${DELAY_SECONDS[anim.delay ?? 'a']}s` }
    : reduced && el.still ? el.still : undefined;
  return (
    <Box
      key={key}
      data-anim={!reduced && anim ? anim.name : undefined}
      data-still={reduced && el.still ? 'true' : undefined}
      sx={[el.style, motion ?? {}] as SxProps<Theme>}
    >
      {el.text}
      {el.children?.map((child, i) => drawElement(child, reduced, i))}
    </Box>
  );
}

export default function WeatherVignette({ code, name }: { code: string; name: string }) {
  const reduced = useCrossingReducedMotion();
  const vignette = vignetteFor(code);
  if (!vignette) return null;
  return (
    // The layer is not keyed, so arrowing between weathers does not replay its entrance; the scene inside is.
    <Box
      aria-hidden="true"
      data-testid="weather-vignette"
      style={{ pointerEvents: 'none' }}
      sx={{
        position: 'absolute', left: '50%', top: '50%', width: `${SCENE_WIDTH}px`, height: `${SCENE_HEIGHT}px`,
        marginLeft: `${-SCENE_WIDTH / 2}px`, marginTop: `${-SCENE_HEIGHT / 2}px`,
        transform: `translate(${SCENE_OFFSET_X}px, ${SCENE_OFFSET_Y}px) scale(${SCENE_SCALE})`,
        filter: CROSSING_WEATHER_SCENE_HALO,
        opacity: 1,
        ...(reduced ? {} : { animation: `${SCENE_IN} 0.8s ease 5s backwards` }),
      }}
    >
      <Box key={code} data-weather={code} sx={{ position: 'absolute', inset: 0 }}>
        <Box
          sx={{
            position: 'absolute', left: 0, right: 0, top: '31%', zIndex: 8, textAlign: 'center',
            fontFamily: SERIF, fontSize: '38px', fontWeight: 600, color: CROSSING_HEAD_TITLE_INK,
            ...(reduced ? { opacity: 1 } : { opacity: 0, animation: `${SCENE_IN} 0.6s ease 0.4s forwards` }),
          }}
        >
          {name}
        </Box>
        {vignette.elements.map((el, i) => drawElement(el, reduced, i))}
      </Box>
    </Box>
  );
}
